import React, { useEffect, useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { InputField } from '../../components/InputField';
import { updateUser, getUserById } from '../../services/AuthService';
import { Link } from 'react-router-dom';

export function UserForm({ isEditMode = false, userId, onSuccess }) {
  const { register, handleSubmit, formState: { errors }, reset, control } = useForm({
    defaultValues: { firstName: '', lastName: '', email: '', phone: '', roles: 'User' }
  });
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isEditMode && userId) {
      setLoading(true);
      getUserById(userId).then((user) => {
        reset({ ...user, roles: user.roles || 'User' });
        setLoading(false);
      }).catch((err) => {
        setError(err.message);
        setLoading(false);
      });
    }
  }, [isEditMode, userId, reset]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (isEditMode) {
        data.id = userId;
      }
      await updateUser(data);
      setLoading(false);
      onSuccess();
      if (!isEditMode) reset();
    } catch (err) {
      setLoading(false);
      setError(`Error saving user: ${err.message}`);
    }
  };

  const roleOptions = ['Guest', 'User', 'Admin'];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md w-full bg-white p-8 rounded-xl shadow-lg">
          <p className="text-red-600 text-center">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 flex items-center justify-center py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-2 bg-white p-8 rounded-xl shadow-lg">
        <h2 className="text-center text-3xl font-extrabold text-gray-900">
          {isEditMode ? 'Edit User' : 'Create User'}
        </h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <InputField
            label="First Name"
            name="firstName"
            type="text"
            register={register}
            error={errors.firstName}
            {...register('firstName', { required: 'First name is required' })}
          />
          <InputField
            label="Last Name"
            name="lastName"
            type="text"
            register={register}
            error={errors.lastName}
            {...register('lastName', { required: 'Last name is required' })}
          />
          <InputField
            label="Email"
            name="email"
            type="email"
            register={register}
            error={errors.email}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email address' }
            })}
          />
          <InputField
            label="Phone Number"
            name="phone"
            type="tel"
            register={register}
            error={errors.phone}
          />
          <div className="space-y-1">
            <label htmlFor="roles" className="block text-sm font-medium text-gray-700 ">
              Role
            </label>
            <Controller
              name="roles"
              control={control}
              rules={{ required: 'Role is required' }}
              render={({ field }) => (
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="block w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-gray-700 text-left focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer shadow-sm"
                    aria-label="Select user role"
                  >
                    {field.value || 'Select a role'}
                    <i className="fas fa-chevron-down absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"></i>
                  </button>
                  {isDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                      {roleOptions.map((role, index) => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => {
                            field.onChange(role.toLowerCase());
                            setIsDropdownOpen(false);
                          }}
                          className={`block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-indigo-100 hover:text-indigo-900 cursor-pointer
                            ${index === 0 ? 'rounded-t-md' : ''} 
                            ${index === roleOptions.length - 1 ? 'rounded-b-md' : ''}`}
                        >
                          {role}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            />
            {errors.roles && (
              <p className="text-red-600 text-sm">{errors.roles.message}</p>
            )}
          </div>
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
            >
              {isEditMode ? 'Update User' : 'Create User'}
            </button>
            <Link
              to="/users"
              type="button"
              className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-800 bg-gray-200 hover:bg-gray-300 text-center"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};